import { faShieldAlt } from '@fortawesome/pro-light-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { CorsoLineItemProperty, CrewMerchantUi, isTruthy } from 'corso-types';

import {
  Action,
  Alert,
  Badge,
  Button,
  Card,
  cn,
  ConfirmModal,
  FloatingSave,
  Icon,
  Modal,
  MultiSelect,
  MultiSelectOption,
  Page,
  SimpleSelect,
  SwitchInput,
} from 'corso-admin-ui';
import { FormEventHandler, useEffect, useRef, useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import EmptyStateAction from '~/components/EmptyStateAction';
import Separator from '~/components/Separator';
import SettingsAnchorCard from '~/features/settings/components/SettingsAnchorCard';
import { useAutomations } from '~/features/settings/hooks/useAutomations';
import { useProtectionRates } from '~/features/settings/hooks/useProtectionRates';
import {
  useHasCheckoutFunctionality,
  useShopifyPlan,
} from '~/features/settings/hooks/useShopifyPlan';
import {
  useConfigSettings,
  useConfigSettingsUpdate,
  useConfigureGsp,
} from '~/hooks/useConfigSettings';
import useIsCorsoAdmin from '~/hooks/useIsCorsoAdmin';
import { shipProtectSettingsFormSchema, UseFormTypes } from '~/types';
import ManageShippingRates from './ManageShippingRates';
import ShippingPlusRules from './ShippingPlusRules';
import ShippingRateForm from './ShippingRateForm';

function useShipProtectSettingsForm() {
  const { data } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection,
  );

  return useForm({
    resolver: zodResolver(shipProtectSettingsFormSchema),
    values: data,
  });
}

function useShipProtectSettingsFormContext() {
  type FormTypes = UseFormTypes<typeof useShipProtectSettingsForm>;
  return useFormContext<
    FormTypes['FieldValues'],
    FormTypes['Context'],
    FormTypes['TransformedValues']
  >();
}

const shipProtectSettingsFormId = 'shipping-protection-settings';

const lineItemPropertyOptions = Object.values(CorsoLineItemProperty).map(
  (v) => ({
    value: v,
    label: v,
  }),
);

function BackupShippingRates({
  show,
  onClose: closeBackupRateSelector,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const methods = useShipProtectSettingsFormContext();
  const { data: protectionRates } = useProtectionRates();

  const protectionRateOptions = protectionRates.map((rate) => ({
    value: `${rate.id}`,
    label: rate.name,
  }));

  const findOption = (values?: number[]) =>
    values
      ?.map((value) =>
        protectionRateOptions.find((option) => option.value === `${value}`),
      )
      .filter(isTruthy) ?? [];

  const backupRatesWIthCarrierService = findOption(
    methods.watch(`widgetConfig.shippingPlus.backupRateIds`, []),
  ).some(
    (option) =>
      protectionRates.find((rate) => rate.id === Number(option.value))
        ?.carrierService,
  );

  return (
    <Modal
      show={show}
      onClose={closeBackupRateSelector}
      title="Backup Shipping Rates"
      actions={
        <>
          <Action onClick={closeBackupRateSelector}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!methods.formState.isDirty}
            form={shipProtectSettingsFormId}
            onClick={() => {
              closeBackupRateSelector();
            }}
          >
            Save
          </Action>
        </>
      }
    >
      {backupRatesWIthCarrierService && (
        <Alert heading="Backup Rates Will Not Apply" variant="warning">
          Backup rates with an attached carrier service will not be applied.
        </Alert>
      )}
      <Controller
        control={methods.control}
        name="widgetConfig.shippingPlus.backupRateIds"
        render={({ field: f, fieldState }) => (
          <MultiSelect
            clearable
            labelVisuallyHidden
            label="Backup Rates"
            options={protectionRateOptions}
            details="The rates to use as a backup if for any reason the Shipping Plus service is unavailable, or delayed. In the event of a failure, or misconfiguration these rates will be used to ensure checkout is still possible."
            placeholder="Select Rates"
            value={findOption(f.value)}
            onChange={(selected) =>
              f.onChange(selected.map(({ value }) => Number(value)))
            }
            error={fieldState.error?.message}
          />
        )}
      />
    </Modal>
  );
}

const rateOptionDefaultValues = {
  all: 'all',
  unassigned: 'unassigned',
} as const;

const getRateOptionsWithCount = ({
  rates,
  rules,
}: {
  rates: CrewMerchantUi.ProtectionRate[];
  rules: CrewMerchantUi.StoreRule[];
}) => {
  const allAssignedStoreShipRateIds = rules
    .map(({ rule }) => rule.event) // extract focused part
    .filter((event) => event.type === 'quoteOrderShipment')
    .flatMap((event) => event.params.storeShipRateIds);

  const unassignedRates = rates.filter(
    (rate) => !allAssignedStoreShipRateIds.includes(rate.id),
  );
  return [
    {
      value: rateOptionDefaultValues.all,
      label: `All Rates (${rates.length})`,
    },
    {
      value: rateOptionDefaultValues.unassigned,
      label: `Not Assigned to a Shipping Geo Zone Rule (${unassignedRates.length})`,
    },
    ...rules.map(({ rule, name, id }) => {
      const storeShipRateIds =
        'storeShipRateIds' in rule.event.params
          ? rule.event.params.storeShipRateIds
          : [];

      const rateCount = rates.filter((rate) =>
        storeShipRateIds.includes(rate.id),
      ).length;

      return {
        value: `${id}`,
        label: `${name} (${rateCount})`,
      };
    }),
  ];
};

function BackupRateAction({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      onClick={onClick}
      variant="ghost"
      icon
      className="relative"
      aria-label="Open backup rates"
    >
      <Icon icon={faShieldAlt} />
      <span
        className={cn(
          'absolute top-0 right-0 flex h-5 w-5 translate-x-1/4 -translate-y-1/4 items-center justify-center rounded-full text-xs font-bold text-white',
          count === 0 ? 'bg-red-500' : 'bg-corso-blue-700',
        )}
        aria-label={`${count} backup rates available`}
      >
        {count}
      </span>
    </Button>
  );
}

function ShippingRates() {
  const { mutate: configureSp, isPending } = useConfigureGsp();

  const { data: protectionRates, refetch: refetchProtectionRates } =
    useProtectionRates();

  const [filteredRates, setFilteredRates] = useState<
    CrewMerchantUi.ProtectionRate[]
  >([]);

  const { data: automations } = useAutomations();

  const quoteShipmentAutomationRules = automations
    .filter((sr) => sr.rule.event.type === 'quoteOrderShipment')
    .sort(
      (a, b) =>
        new Date(b.createdOn).valueOf() - new Date(a.createdOn).valueOf(),
    );

  useEffect(() => {
    setFilteredRates(protectionRates);
  }, [protectionRates]);

  const { data } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection,
  );

  const [showBackupRateSelector, setShowBackupRateSelector] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [hideRates, setShowHideRates] = useState(false);

  const ratesExist = protectionRates.length > 0;

  const backupRatesExist =
    !!data?.widgetConfig.shippingPlus.backupRateIds?.length;

  const shippingRuleOptions = getRateOptionsWithCount({
    rates: protectionRates,
    rules: quoteShipmentAutomationRules,
  });

  const handleShippingRuleSelect = (value: string) => {
    setShowHideRates(false);
    if (value === rateOptionDefaultValues.all) {
      setFilteredRates(protectionRates);
      return;
    }

    if (value === rateOptionDefaultValues.unassigned) {
      const allAssignedStoreShipRateIds = quoteShipmentAutomationRules
        .map(({ rule }) => rule.event) // extract focused part
        .filter((event) => event.type === 'quoteOrderShipment')
        .flatMap((event) => event.params.storeShipRateIds);

      setFilteredRates(
        protectionRates.filter(
          (rate) => !allAssignedStoreShipRateIds.includes(rate.id),
        ),
      );
      return;
    }

    const sr = quoteShipmentAutomationRules.find((r) => r.id === Number(value));
    if (sr?.rule.event.type === 'quoteOrderShipment') {
      const { storeShipRateIds } = sr.rule.event.params;

      setFilteredRates(
        protectionRates.filter((rate) => storeShipRateIds.includes(rate.id)),
      );
    }
  };

  const backupRateCount =
    data?.widgetConfig.shippingPlus.backupRateIds?.length ?? 0;
  return (
    <SettingsAnchorCard id="shipping-rates">
      <div className="flex flex-row items-center justify-between">
        <Card.Heading>Shipping Rates</Card.Heading>

        <div className="flex space-x-2">
          <Action
            icon={hideRates ? 'eye' : 'eyeSlash'}
            onClick={() => setShowHideRates(!hideRates)}
            accessibilityLabel="Collapse Rates"
            variant="ghost"
          />

          <Action
            icon="shopify"
            loading={isPending}
            onClick={() => setShowConfirmationModal(true)}
            accessibilityLabel="Refresh Rates from Shopify"
            variant="ghost"
          />

          <BackupRateAction
            count={backupRateCount}
            onClick={() => setShowBackupRateSelector(true)}
          />

          <Action
            icon="add"
            onClick={() => setShowForm(true)}
            accessibilityLabel="Add Rate"
            variant="ghost"
          />
        </div>

        <ConfirmModal
          prompt="Are you sure you'd like to sync your Shipping Rates and Shipping Zone Rules?"
          title="Sync Rates And Shipping Zones"
          show={showConfirmationModal}
          confirmText="Confirm"
          variant="primary"
          onConfirm={() => {
            configureSp('syncShippingZones');
            setShowConfirmationModal(false);
            refetchProtectionRates().catch(console.error);
          }}
          onCancel={() => setShowConfirmationModal(false)}
        />
        <BackupShippingRates
          show={showBackupRateSelector}
          onClose={() => setShowBackupRateSelector(false)}
        />
      </div>

      {!backupRatesExist && ratesExist && (
        <Alert heading="No Backup Rates Configured" variant="warning">
          To ensure checkout works in case of failure or misconfiguration,
          configure them{' '}
          <Action
            onClick={() => setShowBackupRateSelector(true)}
            variant="link"
            accessibilityLabel="Configure Backup Rates"
          >
            here
          </Action>
          .
        </Alert>
      )}

      <SimpleSelect
        label="Filter by Shipping Geo Zone Rule"
        onChange={(value) => handleShippingRuleSelect(value)}
        options={shippingRuleOptions}
        defaultValue="all"
      />

      <Separator />

      {/* oxlint-disable-next-line no-nested-ternary */}
      {!ratesExist ? (
        <EmptyStateAction.Button
          onClick={() => setShowForm(true)}
          icon={<Icon icon="box" />}
          label="Create A Shipping Rate"
        />
      ) : filteredRates.length === 0 ? (
        <Alert heading="No Shipping Rates Assigned" />
      ) : (
        <ManageShippingRates
          rates={filteredRates}
          editable
          hidden={hideRates}
        />
      )}

      <ShippingRateForm show={showForm} onClose={() => setShowForm(false)} />
    </SettingsAnchorCard>
  );
}

function AppUpgradeRequired() {
  return (
    <Page title="Shipping Plus">
      <Card>
        <Alert
          variant="warning"
          heading="In order to use Shipping Plus, you must upgrade your Corso app to the latest version. This can be done by selecting the Corso app in your Shopify admin and clicking the 'Update' button."
        />
      </Card>
    </Page>
  );
}

function AdvancedSettings({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const methods = useShipProtectSettingsFormContext();
  const { mutate: configureSp } = useConfigureGsp();

  const isApplicationDiscountConfigured = methods.watch(
    'isApplicationDiscountConfigured',
  );

  const [showDiscountModal, setShowDiscountModal] = useState(false);

  return (
    <Modal
      size="xl"
      show={show}
      onClose={onClose}
      title="Advanced Settings"
      actions={
        <>
          <Action onClick={onClose}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!methods.formState.isDirty}
            form={shipProtectSettingsFormId}
            onClick={() => {
              onClose();
            }}
          >
            Save
          </Action>
        </>
      }
    >
      <Alert heading="Corso Admins Only">
        The following settings are only visible to Corso admin users.
      </Alert>

      <Card>
        <Card.Heading>Shipping Plus</Card.Heading>

        <Controller
          control={methods.control}
          name="widgetConfig.shippingPlus.isWidgetDebugEnabled"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="debug-checkout-widget"
              label="Debug Mode"
              details="When enabled, additional debug information will be displayed in the console."
              checked={!!value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="widgetConfig.shippingPlus.isUpdateExistingDiscountsEnabled"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="update-existing-discounts"
              label="Update New And Existing Discounts"
              details="When enabled, existing discounts will be updated to combine with Shipping discounts, and all newly created discounts will also be updated."
              onChange={onChange}
              checked={!!value}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="widgetConfig.shippingPlus.isRateTableCacheEnabled"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="rate-table-cache-configured"
              label="Use Rate Table Cache"
              details="When enabled, calculated shipping rates can be retrieved from a cached rate table. "
              onChange={onChange}
              checked={!!value}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="widgetConfig.shippingPlus.enabledLineItemProperties"
          render={({ field: f, fieldState }) => (
            <MultiSelect
              label="Line Item Properties"
              options={lineItemPropertyOptions}
              details="Line item properties that appear in the order"
              placeholder="Add Attribute"
              value={f.value
                ?.map((value) =>
                  lineItemPropertyOptions.find(
                    (option) => option.value === `${value}`,
                  ),
                )
                .filter(isTruthy)}
              onChange={(o) => f.onChange(o.map((v) => v.value))}
              error={fieldState.error?.message}
            />
          )}
        />

        {isApplicationDiscountConfigured && (
          <div>
            <Action
              onClick={() => setShowDiscountModal(true)}
              variant="primary"
              icon="discount"
            >
              Trigger Bulk Discount Sync
            </Action>
          </div>
        )}
      </Card>

      <Card>
        <div className="flex flex-row justify-between">
          <Card.Heading>Delivery Customization</Card.Heading>

          <Controller
            control={methods.control}
            name="widgetConfig.shippingPlus.deliveryCustomization.isEnabled"
            render={({ field: { onChange, value }, fieldState }) => (
              <SwitchInput
                id="enable-delivery-customization"
                label="Enable Delivery Customization"
                labelVisuallyHidden
                onChange={onChange}
                checked={!!value}
                error={fieldState.error?.message}
              />
            )}
          />
        </div>

        <Controller
          control={methods.control}
          name="widgetConfig.shippingPlus.deliveryCustomization.isReorderShippingLinesEnabled"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="reorder-shipping-lines"
              label="Reorder Shipping Options"
              details="When enabled, the Plus rates will be reordered to appear at the top of the Shipping Options."
              onChange={onChange}
              checked={!!value}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="widgetConfig.shippingPlus.deliveryCustomization.hideRateNames"
          render={({ field: f, fieldState }) => (
            <MultiSelect
              creatable
              label="Hide Shipping Rates (Opt-In)"
              options={[]}
              details="The rate titles to hide at checkout when the customer has opted in to Checkout Plus."
              placeholder="Add Rate"
              value={
                f.value?.map((v) => ({
                  value: v.toString(),
                  label: v,
                })) ?? []
              }
              onChange={(o) => f.onChange(o.map((v) => v.value))}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="widgetConfig.shippingPlus.deliveryCustomization.hideRateNamesOptOut"
          render={({ field: f, fieldState }) => (
            <MultiSelect
              creatable
              label="Hide Shipping Rates (Opt-Out)"
              options={[]}
              details="The rate titles to hide at checkout when the customer has opted out of Checkout Plus."
              placeholder="Add Rate"
              value={
                f.value?.map((v) => ({
                  value: v.toString(),
                  label: v,
                })) ?? []
              }
              onChange={(o) => f.onChange(o.map((v) => v.value))}
              error={fieldState.error?.message}
            />
          )}
        />
      </Card>

      <ConfirmModal
        prompt="Are you sure you'd like trigger a bulk discount sync? This may take a few minutes to complete. Please do not trigger multiple syncs at once."
        title="Bulk Discount Sync"
        show={showDiscountModal}
        confirmText="Confirm"
        variant="primary"
        onConfirm={() => {
          configureSp('bulkUpdateDiscounts');
          setShowDiscountModal(false);
          onClose();
        }}
        onCancel={() => {
          setShowDiscountModal(false);
          onClose();
        }}
      />
    </Modal>
  );
}

function MerchantPaidShippingLines() {
  const { data } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection,
  );

  const merchantPaidShippingLines =
    data?.shipProtectSet.units.filter(
      (unit) => unit.implementationType === 'Shipping_Line',
    ) ?? [];

  if (!merchantPaidShippingLines.length) return null;

  return (
    <Card>
      <Card.Heading>Merchant Paid Shipping Lines</Card.Heading>
      <Alert
        variant="info"
        heading="Orders that contain these shipping titles will be considered merchant paid, regardless of whether Shipping Plus is enabled."
      />

      {merchantPaidShippingLines.map((unit) => (
        <div key={unit.idFromPlatform} className="flex gap-2">
          <div>
            <Badge>{unit.idFromPlatform}</Badge>
          </div>
        </div>
      ))}
    </Card>
  );
}

export default function ShippingPlusSettingsPage() {
  const formRef = useRef<HTMLFormElement>(null);

  const { data } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection,
  );

  const methods = useShipProtectSettingsForm();

  const { mutateAsync: saveChanges } = useConfigSettingsUpdate();

  const submitHandler: FormEventHandler = (event) => {
    methods
      .handleSubmit(
        (values) => saveChanges({ shippingProtection: values }),
        (errors) => {
          console.error(errors);
        },
      )(event)
      .catch(console.error)
      .then(() => {
        methods.reset();
      })
      .catch(console.error);
  };

  const { isShippingLineProtectionOffered, accessScopes } = data ?? {};

  // TODO potentially use the enum values here
  const hasDeliveryCustomizationScope =
    accessScopes?.includes('write_delivery_customizations') ?? false;
  const hasWriteShippingScope =
    accessScopes?.includes('write_shipping') ?? false;

  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const shopifyPlan = useShopifyPlan();
  const showCheckoutSection = useHasCheckoutFunctionality();
  const isCorsoAdmin = useIsCorsoAdmin();

  const displayPlanAlert = shopifyPlan === 'Basic' || shopifyPlan === 'Shopify';

  const isEligible = hasWriteShippingScope && hasDeliveryCustomizationScope;

  if (!isEligible) return <AppUpgradeRequired />;

  // ? this type could be refined
  const cartAttributeWithDescription = {
    _corsoSpTitle: {
      label: 'Shipping Plus Title',
      description: 'The Shipping Plus title applied to the cart.',
    },
    _corsoSpPrice: {
      label: 'Shipping Plus Price',
      description: 'The Shipping Plus price applied to the cart.',
    },
    _corsoSpDiscount: {
      label: 'Shipping Plus Discount',
      description: 'The Shipping Plus discount applied to the cart.',
    },
    _corsoSpDgOptOutOn: {
      label: 'Delivery Guarantee Opt Out On',
      description:
        'The timestamp when the customer opted out of the Delivery Guarantee.',
    },
  } satisfies Record<
    `_corsoSp${string}`,
    { label: string; description: string }
  >;

  const cartAttributeOption = ({
    label,
    description,
    value,
  }: {
    label: string;
    description: string;
    value: string;
  }) => (
    <div className="flex flex-col gap-1">
      <p>
        <span>{label}:</span> <span className="text-neutral-700">{value}</span>
      </p>
      <p className="text-xs text-neutral-500">{description}</p>
    </div>
  );

  const cartAttributesOptions = Object.entries(
    cartAttributeWithDescription,
  ).map(([value, { label, description }]) => ({
    value,
    label: cartAttributeOption({ label: `${label}`, value, description }),
  })) satisfies { value: string; label: MultiSelectOption['label'] }[];

  return (
    <FormProvider {...methods}>
      <Page
        title="Shipping Plus"
        secondaryActions={[
          {
            id: 'show-advanced-settings',
            hidden: !isCorsoAdmin,
            icon: 'settings',
            accessibilityLabel: 'Advanced Settings',
            onAction: () => setShowAdvancedSettings(true),
          },
          {
            id: 'disable-shipping-plus',
            hidden: !isShippingLineProtectionOffered,
            content: 'Turn Off',

            onAction: () => {
              methods.setValue('isShippingLineProtectionOffered', false, {
                shouldDirty: true,
              });
              methods.setValue('widgetConfig.shippingPlus.isEnabled', false, {
                shouldDirty: true,
              });
              formRef.current?.requestSubmit();
            },
          },
        ]}
        primaryAction={
          isShippingLineProtectionOffered
            ? undefined
            : {
                onAction: () => {
                  methods.setValue('isShippingLineProtectionOffered', true, {
                    shouldDirty: true,
                  });
                  methods.setValue(
                    'widgetConfig.shippingPlus.isEnabled',
                    true,
                    {
                      shouldDirty: true,
                    },
                  );
                  formRef.current?.requestSubmit();
                },
                content: 'Turn On',
              }
        }
      >
        <form
          ref={formRef}
          className="flex flex-col gap-4"
          id={shipProtectSettingsFormId}
          onSubmit={submitHandler}
          onReset={(e) => {
            e.preventDefault();
            methods.reset();
          }}
        >
          <div className="flex flex-col gap-4">
            <FloatingSave
              isDirty={methods.formState.isDirty}
              isSubmitting={methods.formState.isSubmitting}
              form={shipProtectSettingsFormId}
            />

            <MerchantPaidShippingLines />

            {displayPlanAlert && (
              <Alert variant="info" heading="Shopify Plan Limitation" elevated>
                Your Shopify plan may not support the features required to use
                Shipping Plus. Please contact us for more information.
              </Alert>
            )}

            <ShippingPlusRules />
          </div>

          <ShippingRates />

          {showCheckoutSection && isShippingLineProtectionOffered && (
            <SettingsAnchorCard id="checkout-extension">
              <Card.Heading>Checkout Extension</Card.Heading>

              <Controller
                control={methods.control}
                name="widgetConfig.shippingPlus.isFreeShippingOptInEnabled"
                render={({ field: { onChange, value }, fieldState }) => (
                  <SwitchInput
                    id="tos-shipping-checkbox"
                    label="Offer Shipping Discounts"
                    details="When enabled, you will be able to setup shipping discount modifications."
                    onChange={onChange}
                    checked={!!value}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                control={methods.control}
                name="widgetConfig.shippingPlus.enabledCartAttributes"
                render={({ field: f, fieldState }) => (
                  <MultiSelect
                    label="Cart Attributes"
                    options={cartAttributesOptions}
                    details="Cart attributes that appear in the order’s
                        Additional Details after checkout. More attributes
                        may be added based on enabled Shipping Plus
                        features"
                    placeholder="Add Attribute"
                    value={f.value
                      ?.map((value) =>
                        cartAttributesOptions.find(
                          (option) => option.value === `${value}`,
                        ),
                      )
                      .filter(isTruthy)}
                    onChange={(o) => f.onChange(o.map((v) => v.value))}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </SettingsAnchorCard>
          )}
        </form>
      </Page>
      <AdvancedSettings
        show={showAdvancedSettings}
        onClose={() => setShowAdvancedSettings(false)}
      />
    </FormProvider>
  );
}
